import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

interface NoteEvent {
  type: 'note:created' | 'note:updated' | 'note:deleted' | 'note:archived';
  noteId: number;
  timestamp: string;
}

interface UseNoteEventsOptions {
  /** Called when the currently-open note is updated externally */
  onExternalUpdate?: (noteId: number) => void;
}

/**
 * SSE hook for real-time note change notifications.
 * Invalidates React Query caches when notes change from external sources
 * (filesystem edits, other tabs, metadata janitor).
 *
 * Mount at the NotesLayout level so it's active on all note pages.
 */
export function useNoteEvents(options?: UseNoteEventsOptions) {
  const queryClient = useQueryClient();
  const eventSourceRef = useRef<EventSource | null>(null);
  const onExternalUpdateRef = useRef(options?.onExternalUpdate);

  // Keep callback ref up to date without re-creating effect
  useEffect(() => {
    onExternalUpdateRef.current = options?.onExternalUpdate;
  }, [options?.onExternalUpdate]);

  useEffect(() => {
    const es = new EventSource('/api/notes/events', { withCredentials: true });
    eventSourceRef.current = es;

    es.onmessage = (event) => {
      try {
        const data: NoteEvent = JSON.parse(event.data);

        switch (data.type) {
          case 'note:created':
            // Refresh the notes list in sidebar
            queryClient.invalidateQueries({ queryKey: ['notes'] });
            break;

          case 'note:updated':
            // Refresh both the specific note and the list
            queryClient.invalidateQueries({ queryKey: ['note', data.noteId] });
            queryClient.invalidateQueries({ queryKey: ['notes'] });
            // Notify editor if this note is open
            onExternalUpdateRef.current?.(data.noteId);
            break;

          case 'note:deleted':
          case 'note:archived':
            // Refresh the notes list
            queryClient.invalidateQueries({ queryKey: ['notes'] });
            queryClient.invalidateQueries({ queryKey: ['note', data.noteId] });
            break;
        }
      } catch {
        // Ignore malformed events
      }
    };

    es.onerror = () => {
      // EventSource auto-reconnects — no action needed
      // Browser will retry with exponential backoff
    };

    return () => {
      es.close();
      eventSourceRef.current = null;
    };
  }, [queryClient]);
}
